// frontend/src/pages/KycVerificationPage.js
// Web port of the mobile KYC screen (mobile/src/screens/KycVerificationScreen.js).
// Backend contract: GET /api/kyc/status and POST /api/kyc/initiate {idType,idNumber}
// both return { status, level, rejectionReason }. Keep in step with mobile.
import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, ShieldCheck, CheckCircle, Clock, AlertCircle, Lock, Zap, FileText } from 'lucide-react';
import { apiRequest } from '../utils/settlementUtils';

const ID_TYPES = [
  { value: 'BVN', label: 'BVN', hint: 'Bank Verification Number' },
  { value: 'NIN', label: 'NIN', hint: 'National Identity Number' },
];

const isVerified = (s) => (s || '').toLowerCase() === 'verified';
const isRejected = (s) => (s || '').toLowerCase() === 'rejected';
const isPending = (s) => (s || '').toLowerCase() === 'pending';

const KycVerificationPage = ({ user, onBack }) => {
  const [idType, setIdType] = useState('BVN');
  const [idNumber, setIdNumber] = useState('');
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [kyc, setKyc] = useState(null); // last KycStatusDto from the backend

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const s = await apiRequest('/kyc/status');
      setKyc(s);
    } catch {
      // No status yet / not reachable -> fall through to the default banner.
    }
    setLoadingStatus(false);
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const status = kyc?.status;
  const canSubmit = idNumber.length === 11 && !submitting;

  const submit = async () => {
    if (idNumber.length !== 11 || submitting) return;
    setSubmitting(true);
    try {
      const result = await apiRequest('/kyc/initiate', {
        method: 'POST',
        body: JSON.stringify({ idType, idNumber }),
      });
      setKyc(result);
      if (isVerified(result?.status)) setIdNumber(''); // keep the number on a rejection so they can retry
    } catch (err) {
      setKyc((prev) => ({ ...(prev || {}), status: 'Rejected', rejectionReason: err.message }));
    } finally {
      setSubmitting(false);
    }
  };

  const chooseType = (value) => {
    if (value === idType) return;
    setIdType(value);
    setIdNumber('');
  };

  const banner = () => {
    if (loadingStatus) return null;
    if (isVerified(status)) {
      return (
        <Banner color="#10b981" icon={<CheckCircle size={22} color="#10b981" />} title="You're verified">
          Your identity is confirmed. You can deposit and withdraw without limits.
        </Banner>
      );
    }
    if (isPending(status)) {
      return (
        <Banner color="#f59e0b" icon={<Clock size={22} color="#f59e0b" />} title="Verification pending">
          We couldn't confirm your details just yet. Please try again in a moment.
        </Banner>
      );
    }
    if (isRejected(status)) {
      return (
        <Banner color="#ef4444" icon={<AlertCircle size={22} color="#ef4444" />} title="Verification failed">
          {kyc?.rejectionReason || 'We couldn’t verify that ID. Please check the details and try again.'}
        </Banner>
      );
    }
    return (
      <Banner color="#60a5fa" icon={<ShieldCheck size={22} color="#60a5fa" />} title="Verify your identity">
        Required by law before you can deposit or withdraw. It only takes a moment.
      </Banner>
    );
  };

  return (
    <div style={S.container}>
      <div style={S.header}>
        <button onClick={onBack} style={S.backBtn} aria-label="Back">
          <ArrowLeft size={20} color="white" />
        </button>
        <div>
          <h1 style={S.headerTitle}>Verify Identity</h1>
          <p style={S.headerSubtitle}>KYC verification</p>
        </div>
      </div>

      <div style={S.body}>
        {banner()}

        {!isVerified(status) && (
          <>
            <div style={S.sectionTitle}>ID type</div>
            <div style={S.toggleRow}>
              {ID_TYPES.map((t) => {
                const active = idType === t.value;
                return (
                  <button
                    key={t.value}
                    onClick={() => chooseType(t.value)}
                    style={{ ...S.toggle, ...(active ? S.toggleActive : {}) }}
                  >
                    <div style={{ ...S.toggleLabel, ...(active ? S.toggleLabelActive : {}) }}>{t.label}</div>
                    <div style={{ ...S.toggleHint, ...(active ? S.toggleHintActive : {}) }}>{t.hint}</div>
                  </button>
                );
              })}
            </div>

            <div style={{ ...S.sectionTitle, marginTop: 24 }}>{idType} number</div>
            <input
              style={S.input}
              value={idNumber}
              onChange={(e) => setIdNumber(e.target.value.replace(/[^0-9]/g, '').slice(0, 11))}
              onKeyDown={(e) => e.key === 'Enter' && canSubmit && submit()}
              placeholder={`Enter your 11-digit ${idType}`}
              inputMode="numeric"
              maxLength={11}
              disabled={submitting}
            />
            <div style={S.helper}>{idNumber.length}/11 digits</div>

            <button onClick={submit} disabled={!canSubmit} style={{ ...S.submit, opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}>
              {submitting ? 'Verifying…' : `Verify ${idType}`}
            </button>
          </>
        )}

        {isVerified(status) && (
          <button onClick={onBack} style={{ ...S.submit, background: 'linear-gradient(135deg, #7c3aed, #2563eb)' }}>
            Done
          </button>
        )}

        <div style={S.infoList}>
          <InfoRow icon={<Lock size={20} color="#60a5fa" />} title="Secure"
            text="Your ID is sent over an encrypted connection and used only to verify you." />
          <InfoRow icon={<Zap size={20} color="#10b981" />} title="Instant"
            text="Most checks complete in seconds against the BVN/NIN database." />
          <InfoRow icon={<FileText size={20} color="#f59e0b" />} title="Why we ask"
            text="Identity verification is a legal requirement and helps keep your account safe." />
        </div>
      </div>
    </div>
  );
};

const Banner = ({ color, icon, title, children }) => (
  <div
    style={{
      display: 'flex',
      gap: 12,
      alignItems: 'flex-start',
      borderRadius: 14,
      border: `1px solid ${color}40`,
      backgroundColor: `${color}14`,
      padding: 16,
      marginBottom: 24,
    }}
  >
    <div style={{ flexShrink: 0 }}>{icon}</div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 13, color: '#cbd5e1', lineHeight: '18px' }}>{children}</div>
    </div>
  </div>
);

const InfoRow = ({ icon, title, text }) => (
  <div style={S.infoCard}>
    <div style={{ flexShrink: 0 }}>{icon}</div>
    <div style={{ flex: 1 }}>
      <div style={S.infoTitle}>{title}</div>
      <div style={S.infoText}>{text}</div>
    </div>
  </div>
);

const S = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#000',
    color: 'white',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    background: 'linear-gradient(135deg, #7c3aed, #2563eb)',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '16px 20px 18px',
  },
  backBtn: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    border: 'none',
    borderRadius: 8,
    padding: 8,
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
  },
  headerTitle: { fontSize: 20, fontWeight: 'bold', margin: 0 },
  headerSubtitle: { fontSize: 12, color: 'rgba(255,255,255,0.8)', margin: '2px 0 0' },

  body: { padding: 16, maxWidth: 560, margin: '0 auto', boxSizing: 'border-box' },

  sectionTitle: { fontSize: 16, fontWeight: 600, color: 'white', marginBottom: 12 },

  toggleRow: { display: 'flex', gap: 12 },
  toggle: {
    flex: 1,
    backgroundColor: '#111827',
    border: '2px solid #374151',
    borderRadius: 12,
    padding: '14px 12px',
    cursor: 'pointer',
    textAlign: 'center',
  },
  toggleActive: { backgroundColor: 'rgba(37,99,235,0.15)', borderColor: '#2563eb' },
  toggleLabel: { fontSize: 16, fontWeight: 700, color: '#9ca3af' },
  toggleLabelActive: { color: 'white' },
  toggleHint: { fontSize: 11, color: '#6b7280', marginTop: 3 },
  toggleHintActive: { color: '#93c5fd' },

  input: {
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: '#111827',
    border: '1px solid #374151',
    borderRadius: 12,
    padding: '14px 16px',
    color: 'white',
    fontSize: 18,
    fontWeight: 600,
    letterSpacing: 2,
    outline: 'none',
  },
  helper: { fontSize: 12, color: '#6b7280', marginTop: 6 },

  submit: {
    width: '100%',
    marginTop: 24,
    padding: '16px',
    background: 'linear-gradient(135deg, #10b981, #059669)',
    color: 'white',
    fontSize: 16,
    fontWeight: 700,
    border: 'none',
    borderRadius: 12,
    cursor: 'pointer',
  },

  infoList: { display: 'flex', flexDirection: 'column', gap: 12, marginTop: 28, paddingBottom: 28 },
  infoCard: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    backgroundColor: '#1f2937',
    border: '1px solid #374151',
    borderRadius: 12,
    padding: 16,
  },
  infoTitle: { fontSize: 14, fontWeight: 600, color: 'white', marginBottom: 3 },
  infoText: { fontSize: 12, color: '#9ca3af', lineHeight: '17px' },
};

export default KycVerificationPage;
